import { useEffect, useState, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { cachedGet, peek, rememberListings } from "@/lib/cache";
import { unwrapListings } from "@/lib/adapters";
import PropertyCard from "@/components/PropertyCard";
import FeaturedCarousel from "@/components/FeaturedCarousel";

const GuineaMap = lazy(() => import("@/components/GuineaMap"));
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  MagnifyingGlass,
  House,
  Buildings,
  MapTrifold,
  Storefront,
  HardHat,
  Briefcase,
  ArrowRight,
  ShieldCheck,
  Lightning,
  Handshake,
} from "@phosphor-icons/react";

const CATEGORIES = [
  { key: "maison", label: "Maison", icon: House, hint: "Villas & duplex" },
  { key: "appartement", label: "Appartement", icon: Buildings, hint: "Studios & F2/F3" },
  { key: "terrain", label: "Terrain", icon: MapTrifold, hint: "Nu & titré" },
  { key: "bureau", label: "Bureau", icon: Briefcase, hint: "Open-space & coworking" },
  { key: "commercial", label: "Local commercial", icon: Storefront, hint: "Boutiques & magasins" },
  { key: "chantier", label: "Chantier", icon: HardHat, hint: "En construction" },
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1745761320791-5ae142edee8c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yJTIwdHJvcGljYWx8ZW58MHx8fHwxNzg0NjY2OTIwfDA&ixlib=rb-4.1.0&q=85";

// Hero cinematic layers — a Ken-Burns pan+zoom on a stack of coastal/urban
// stills gives the "aerial drone" feeling of the desired video hero without
// relying on third-party CDN videos (Pexels/Pixabay block hotlinking).
const HERO_W = typeof window !== "undefined" && window.innerWidth < 768 ? 900 : 1600;
const HERO_LAYERS = [
  "photo-1745761320791-5ae142edee8c",
  "photo-1502672260266-1c1ef2d93688",
  "photo-1568605114967-8130f3a36994",
].map((p) => `https://images.unsplash.com/${p}?auto=format&fit=crop&w=${HERO_W}&q=65`);

const FEATURED_Q = { limit: 8, section: "featured" };
const RECENT_Q = { limit: 6 };

export default function HomePage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [transactionType, setTransactionType] = useState("vente");
  const [featured, setFeatured] = useState(() => unwrapListings(peek("/listings", FEATURED_Q)).items);
  const [recent, setRecent] = useState(() => unwrapListings(peek("/listings", RECENT_Q)).items);
  const [loadingRecent, setLoadingRecent] = useState(() => !peek("/listings", RECENT_Q));
  const stats = { total: recent.length };

  useEffect(() => {
    cachedGet("/listings", FEATURED_Q)
      .then((d) => {
        const { items } = unwrapListings(d);
        rememberListings(items);
        setFeatured(items);
      })
      .catch(() => {});
    cachedGet("/listings", RECENT_Q)
      .then((d) => {
        const { items } = unwrapListings(d);
        rememberListings(items);
        setRecent(items);
      })
      .catch(() => {})
      .finally(() => setLoadingRecent(false));
  }, []);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (transactionType) params.set("transaction_type", transactionType);
    navigate(`/rechercher?${params.toString()}`);
  };

  const listToShow = recent;

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-secondary text-secondary-foreground">
        <div className="grain-overlay absolute inset-0 z-20 pointer-events-none" />
        {/* Warm dark overlay for readability */}
        <div
          aria-hidden
          className="absolute inset-0 z-10 bg-gradient-to-br from-secondary/85 via-secondary/70 to-secondary/85"
        />
        <div
          className="absolute inset-0 z-0 h-full w-full"
          data-testid="hero-video"
          aria-hidden="true"
        >
          {HERO_LAYERS.map((src, i) => (
            <img
              key={src}
              src={src}
              alt=""
              decoding="async"
              fetchPriority={i === 0 ? "high" : "low"}
              loading={i === 0 ? "eager" : "lazy"}
              className="hero-kenburns absolute inset-0 h-full w-full object-cover opacity-0 mix-blend-luminosity"
              style={{
                animationDelay: `${i * 6}s`,
                animationDuration: `${HERO_LAYERS.length * 6}s`,
              }}
            />
          ))}
        </div>
        <div className="relative z-30 mx-auto grid max-w-7xl gap-10 px-4 pb-16 pt-14 md:px-8 md:pb-24 md:pt-24 lg:grid-cols-12">
          <div className="fade-in-up flex flex-col justify-center lg:col-span-8">
            <span className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" /> N°1 en République de Guinée
            </span>
            <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Trouve ta maison à
              <span className="ml-3 inline-block rounded-2xl bg-primary px-3 text-primary-foreground">
                Conakry
              </span>
              <br />
              et partout en Guinée.
            </h1>
            <p className="mt-5 max-w-xl text-base text-white/80 md:text-lg">
              Des milliers d'annonces vérifiées — maisons, appartements, terrains et locaux commerciaux.
              Contactez directement le propriétaire par téléphone ou WhatsApp.
            </p>

            <form
              onSubmit={submitSearch}
              className="mt-8 flex w-full max-w-2xl flex-col gap-3 rounded-2xl border border-white/10 bg-background/95 p-3 text-foreground shadow-2xl backdrop-blur md:flex-row md:items-center md:rounded-full md:p-2"
              data-testid="hero-search-form"
            >
              <div className="flex items-center gap-1 rounded-full bg-muted p-1">
                {[
                  { key: "vente", label: "Acheter" },
                  { key: "location", label: "Louer" },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    data-testid={`hero-tx-${t.key}`}
                    onClick={() => setTransactionType(t.key)}
                    className={`btn-pill rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider ${
                      transactionType === t.key
                        ? "bg-primary text-primary-foreground shadow"
                        : "text-muted-foreground"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-1 items-center gap-2 px-2">
                <MagnifyingGlass size={20} className="text-muted-foreground" />
                <Input
                  data-testid="hero-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ville, quartier ou mot-clé (ex: Kaloum, villa piscine…)"
                  className="border-0 bg-transparent text-base shadow-none focus-visible:ring-0"
                />
              </div>
              <Button
                type="submit"
                data-testid="hero-search-submit"
                className="btn-pill rounded-full bg-primary px-6 font-bold text-primary-foreground hover:bg-primary/90"
              >
                Rechercher
                <ArrowRight size={16} weight="bold" className="ml-1" />
              </Button>
            </form>

            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} weight="duotone" className="text-primary" />
                Annonces vérifiées
              </div>
              <div className="flex items-center gap-2">
                <Lightning size={20} weight="duotone" className="text-primary" />
                Contact direct WhatsApp
              </div>
              <div className="flex items-center gap-2">
                <Handshake size={20} weight="duotone" className="text-primary" />
                Sans intermédiaire
              </div>
            </div>
          </div>

          <div className="hidden lg:col-span-4 lg:flex lg:flex-col lg:justify-end">
            <div className="ml-auto w-72 rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-lg">
              <div className="font-display text-5xl font-black">{stats.total || "1000+"}</div>
              <div className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-white/70">
                Annonces disponibles
              </div>
              <div className="mt-4 h-px bg-white/20" />
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-white/70">Villes couvertes</span>
                <span className="font-bold">12+</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED CAROUSEL (boosted listings) */}
      {featured.length > 0 && <FeaturedCarousel items={featured} />}

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-primary">Catégories</span>
            <h2 className="mt-2 font-display text-3xl font-black md:text-4xl">Que cherchez-vous ?</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5 md:gap-6">
          {CATEGORIES.map((cat) => (
            <Link
              key={cat.key}
              to={`/rechercher?category=${cat.key}`}
              data-testid={`category-card-${cat.key}`}
              className="card-motion group flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 hover:border-primary"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-secondary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <cat.icon size={26} weight="duotone" />
              </div>
              <div>
                <div className="font-display text-lg font-bold">{cat.label}</div>
                <div className="text-xs text-muted-foreground">{cat.hint}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* RECENT / FEATURED */}
      <section className="mx-auto max-w-7xl px-4 pb-16 md:px-8 md:pb-24">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-primary">Récentes</span>
            <h2 className="mt-2 font-display text-3xl font-black md:text-4xl">Nouvelles annonces</h2>
          </div>
          <Link
            to="/rechercher"
            className="btn-pill hidden items-center gap-1 rounded-full border border-border px-4 py-2 text-sm font-bold hover:bg-secondary hover:text-secondary-foreground md:inline-flex"
            data-testid="see-all-listings"
          >
            Voir tout <ArrowRight size={16} />
          </Link>
        </div>

        {loadingRecent && listToShow.length === 0 ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="recent-skeleton">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-96 animate-pulse rounded-2xl bg-muted" />
            ))}
          </div>
        ) : listToShow.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent">
              <House size={26} weight="duotone" className="text-secondary" />
            </div>
            <h3 className="mt-4 font-display text-xl font-bold">Aucune annonce pour le moment</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Soyez le premier à publier votre bien immobilier !
            </p>
            <Button
              onClick={() => navigate("/publier")}
              data-testid="empty-publish-cta"
              className="btn-pill mt-6 rounded-full bg-primary font-bold text-primary-foreground hover:bg-primary/90"
            >
              Publier une annonce
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {listToShow.map((l) => (
              <PropertyCard key={l.listing_id} listing={l} />
            ))}
          </div>
        )}
      </section>

      {/* CTA Publish */}
      <section className="mx-auto max-w-7xl px-4 pb-16 md:px-8 md:pb-24" data-testid="map-section">
        <div className="mb-6 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-primary">Carte</span>
            <h2 className="mt-2 font-display text-3xl font-black md:text-4xl">Les annonces partout en Guinée</h2>
            <p className="mt-1 text-sm text-muted-foreground">Touchez une préfecture pour voir ses biens.</p>
          </div>
          <Link to="/alertes" data-testid="map-alert-link" className="text-sm font-bold text-primary underline-offset-4 hover:underline">
            Créer une alerte pour ma préfecture →
          </Link>
        </div>
        <Suspense fallback={<div className="h-[440px] animate-pulse rounded-3xl bg-muted" />}>
          <GuineaMap />
        </Suspense>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 md:px-8 md:pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-primary p-8 text-primary-foreground md:p-14">
          <div className="grain-overlay absolute inset-0" />
          <div className="relative grid gap-6 md:grid-cols-2 md:items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary-foreground/70">
                Propriétaire / Agence
              </span>
              <h3 className="mt-3 font-display text-3xl font-black md:text-4xl">
                Publiez votre bien gratuitement en 2 minutes.
              </h3>
              <p className="mt-3 max-w-lg text-primary-foreground/90">
                Photos, prix, description — recevez des appels et messages WhatsApp directement des acheteurs.
              </p>
            </div>
            <div className="flex justify-start md:justify-end">
              <Button
                onClick={() => navigate("/publier")}
                data-testid="cta-publish-btn"
                size="lg"
                className="btn-pill rounded-full bg-background px-8 py-6 text-base font-bold text-foreground hover:bg-background/90"
              >
                Publier maintenant <ArrowRight size={18} className="ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
